/**
 * User API Client
 *
 * Implements UserApiClient using tRPC to communicate with the kernel backend.
 * Uses members.* and role.* procedures.
 */

import type {
  AcceptInvitationResult,
  AcceptOwnershipTransferResult,
  InvitationDetails,
  InviteMemberInput,
  Member,
  MemberListFilters,
  MemberListResult,
  MemberStats,
  MemberStatus,
  MembershipRole,
  OwnershipTransferTokenDetails,
  PendingEmailChange,
  PendingOwnershipTransfer,
  RequestEmailChangeResult,
  Role,
  SignInMethod,
  UpdateProfileInput,
  UserApiClient,
  VerifyEmailChangeResult,
} from '@xhubio-saas/core-frontend/plugin-user'
import { useMemo, useRef } from 'react'
import { trpc } from '@/trpc'
import { entwertePostausgang } from './postausgang'

/**
 * @param targetUserId Um **wen** es auf dieser Seite geht — die Person, deren
 * Profil `updateProfile` schreibt und deren Adresse `requestEmailChange`
 * wechselt.
 *
 * 🔴 Ohne diesen Wert arbeitet der Server auf dem **Anmelder**, nicht auf der
 * Person, die gerade im Formular steht. Beide Prozeduren loesten die betroffene
 * Person frueher allein aus der Sitzung auf, das Formular laedt den Datensatz
 * aber aus der URL. Die Folgen waren verschieden schwer:
 *
 *  - `updateProfile`: ein Owner, der einen Kollegen pflegte, ueberschrieb still
 *    sein EIGENES Profil (Plan API 2026-08-20/21).
 *  - `requestEmailChange`: der Owner bekam **beide** Mails selbst —
 *    Bestaetigungslink und Sicherheitshinweis —, der Kollege erfuhr nichts, und
 *    ein Klick haette die Anmeldeadresse des Owners gewechselt
 *    (Plan API 2026-08-21/05).
 *
 * ⚪ Weggelassen heisst weiterhin „ich selbst". Alle anderen Seiten rufen den
 * Client deshalb unveraendert ohne Argument auf; nur
 * `UserDetailPageWithProviders` (core-frontend 4.0.0: die Detailseite IST die
 * Formularseite, seit dem Bump gibt es keine separate `/users/:userId/edit`-
 * Route mehr) reicht die Route-Id herein.
 *
 * 🔴 Serverseitig darf den Adresswechsel eines anderen **nur der Owner**
 * ausloesen (nicht `admin`) — die Mutation antwortet sonst mit FORBIDDEN.
 */
export function useUserClient(targetUserId?: string): UserApiClient {
  const utils = trpc.useUtils()
  const inviteMutation = trpc.members.invite.useMutation()
  const activateMutation = trpc.members.activate.useMutation()
  const deactivateMutation = trpc.members.deactivate.useMutation()
  const updateProfileMutation = trpc.members.updateProfile.useMutation()
  const acceptInvitationMutation = trpc.members.acceptInvitation.useMutation()
  const assignRoleMutation = trpc.role.assignToUser.useMutation()
  const removeRoleMutation = trpc.role.removeFromUser.useMutation()
  const requestEmailChangeMutation = trpc.members.requestEmailChange.useMutation()
  const verifyEmailChangeMutation = trpc.members.verifyEmailChange.useMutation()
  const cancelEmailChangeMutation = trpc.members.cancelEmailChange.useMutation()
  const cancelEmailChangeByTokenMutation = trpc.members.cancelEmailChangeByToken.useMutation()
  const unlinkSignInMethodMutation = trpc.members.unlinkSignInMethod.useMutation()
  const requestOwnershipTransferMutation = trpc.members.requestOwnershipTransfer.useMutation()
  const cancelOwnershipTransferMutation = trpc.members.cancelOwnershipTransfer.useMutation()
  const acceptOwnershipTransferMutation = trpc.members.acceptOwnershipTransfer.useMutation()
  const rejectOwnershipTransferMutation = trpc.members.rejectOwnershipTransfer.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({
    inviteMutation,
    activateMutation,
    deactivateMutation,
    updateProfileMutation,
    acceptInvitationMutation,
    assignRoleMutation,
    removeRoleMutation,
    requestEmailChangeMutation,
    verifyEmailChangeMutation,
    cancelEmailChangeMutation,
    cancelEmailChangeByTokenMutation,
    unlinkSignInMethodMutation,
    requestOwnershipTransferMutation,
    cancelOwnershipTransferMutation,
    acceptOwnershipTransferMutation,
    rejectOwnershipTransferMutation,
  })
  mutations.current = {
    inviteMutation,
    activateMutation,
    deactivateMutation,
    updateProfileMutation,
    acceptInvitationMutation,
    assignRoleMutation,
    removeRoleMutation,
    requestEmailChangeMutation,
    verifyEmailChangeMutation,
    cancelEmailChangeMutation,
    cancelEmailChangeByTokenMutation,
    unlinkSignInMethodMutation,
    requestOwnershipTransferMutation,
    cancelOwnershipTransferMutation,
    acceptOwnershipTransferMutation,
    rejectOwnershipTransferMutation,
  }

  return useMemo(
    () => ({
      // ═══════════════════════════════════════════════════════════════════
      // Members
      // ═══════════════════════════════════════════════════════════════════

      async listMembers(filters: MemberListFilters): Promise<MemberListResult> {
        await utils.members.list.invalidate()
        const response = await utils.members.list.fetch({
          search: filters.search,
          status: filters.status,
          limit: filters.pageSize,
          offset: (filters.page - 1) * filters.pageSize,
        })

        // biome-ignore lint/suspicious/noExplicitAny: tRPC response is untyped
        const items: Member[] = (response.users ?? []).map((u: any) => ({
          id: u.id,
          userId: u.id,
          email: u.email,
          name: u.name ?? null,
          firstName: u.firstName ?? null,
          lastName: u.lastName ?? null,
          phone: u.phone ?? null,
          street: u.street ?? null,
          city: u.city ?? null,
          zip: u.zip ?? null,
          country: u.country ?? null,
          status: u.status as MemberStatus,
          role: (u.role ?? 'member') as MembershipRole,
          lastLoginAt: u.lastLoginAt ? new Date(u.lastLoginAt) : null,
          createdAt: new Date(u.createdAt),
        }))

        return { items, total: response.total ?? items.length }
      },

      async getMember(memberId: string): Promise<Member> {
        await utils.members.get.invalidate({ userId: memberId })
        const u = await utils.members.get.fetch({ userId: memberId })
        return {
          id: u.id,
          userId: u.id,
          email: u.email,
          name: u.name ?? null,
          firstName: u.firstName ?? null,
          lastName: u.lastName ?? null,
          phone: u.phone ?? null,
          street: u.street ?? null,
          city: u.city ?? null,
          zip: u.zip ?? null,
          country: u.country ?? null,
          status: u.status as MemberStatus,
          role: (u.role ?? 'member') as MembershipRole,
          lastLoginAt: u.lastLoginAt ? new Date(u.lastLoginAt) : null,
          createdAt: new Date(u.createdAt),
        }
      },

      async getMemberStats(): Promise<MemberStats> {
        await utils.members.stats.invalidate()
        return utils.members.stats.fetch()
      },

      async inviteMember(input: InviteMemberInput): Promise<void> {
        await mutations.current.inviteMutation.mutateAsync({
          email: input.email,
          name: input.name,
          roleIds: input.roleIds,
          // Sprache des Einladenden (core-frontend >= 5.124.0) waehlt die Mailvorlage (KW-005)
          ...(input.locale ? { locale: input.locale } : {}),
        })
        // Die Einladungs-Mail des Kernels (Kategorie `org`) kann in der Freigabe liegen.
        await entwertePostausgang(utils)
      },

      async activateMember(userId: string): Promise<void> {
        await mutations.current.activateMutation.mutateAsync({ userId })
      },

      async deactivateMember(userId: string): Promise<void> {
        await mutations.current.deactivateMutation.mutateAsync({ userId })
      },

      // ═══════════════════════════════════════════════════════════════════
      // Profile
      // ═══════════════════════════════════════════════════════════════════

      async updateProfile(input: UpdateProfileInput): Promise<void> {
        // Die Ziel-Id gehoert in die Nutzlast, nicht in die Sitzung — siehe
        // Kopfkommentar dieses Hooks.
        await mutations.current.updateProfileMutation.mutateAsync({
          ...input,
          targetUserId,
        })
        // Invalidate member caches so detail page shows fresh data
        await utils.members.get.invalidate()
        await utils.members.list.invalidate()
      },

      // ═══════════════════════════════════════════════════════════════════
      // Roles
      // ═══════════════════════════════════════════════════════════════════

      async listRoles(): Promise<Role[]> {
        await utils.role.list.invalidate()
        const response = await utils.role.list.fetch({})
        // biome-ignore lint/suspicious/noExplicitAny: tRPC response is untyped
        return (response ?? []).map((r: any) => ({
          id: r.id,
          name: r.name,
          description: r.description ?? null,
          isSystem: r.isSystem ?? false,
          isDefault: r.isDefault ?? false,
        }))
      },

      async getUserRoles(userId: string): Promise<Role[]> {
        await utils.role.getUserRoles.invalidate({ userId })
        const response = await utils.role.getUserRoles.fetch({
          userId,
        })
        // biome-ignore lint/suspicious/noExplicitAny: tRPC response is untyped
        return (response ?? []).map((r: any) => ({
          id: r.id,
          name: r.name,
          description: r.description ?? null,
          isSystem: r.isSystem ?? false,
          isDefault: r.isDefault ?? false,
        }))
      },

      async assignRole(userId: string, roleId: string): Promise<void> {
        await mutations.current.assignRoleMutation.mutateAsync({ userId, roleId })
      },

      async removeRole(userId: string, roleId: string): Promise<void> {
        await mutations.current.removeRoleMutation.mutateAsync({ userId, roleId })
      },

      // ═══════════════════════════════════════════════════════════════════
      // Invitations (public)
      // ═══════════════════════════════════════════════════════════════════

      async getInvitationByToken(token: string): Promise<InvitationDetails> {
        const response = await utils.members.getInvitationByToken.fetch({ token })
        return {
          email: response.email,
          // `role` ist die better-auth-Mitgliedschaft, `roles` die im Einladungsdialog gewählten
          // RBAC-Rollen. Ohne `roles` zeigt die Annahmeseite „Mitglied" statt „Betrachter"
          // (BLOCKED-DEFERRED dashandwerk_0827-07, wie verein `userClient.ts`).
          role: response.role ?? null,
          roles: response.roles ?? [],
          inviterName: response.inviterName ?? null,
          organization: {
            id: response.organization.id,
            name: response.organization.name,
            logo: response.organization.logo ?? null,
          },
          expiresAt: new Date(response.expiresAt),
          userExists: response.userExists ?? false,
          existingUserName: response.existingUserName ?? null,
        }
      },

      async acceptInvitation(token: string): Promise<AcceptInvitationResult> {
        return mutations.current.acceptInvitationMutation.mutateAsync({ token })
      },

      // ═══════════════════════════════════════════════════════════════════
      // Email change (self-service)
      // ═══════════════════════════════════════════════════════════════════

      /**
       * 🔴 `targetUserId` geht mit — sonst wechselt der Server die Adresse des
       * ANMELDERS und schickt beide Mails an ihn. Auf `/users/:userId/edit`
       * steht aber der Kollege im Formular.
       */
      async requestEmailChange(newEmail: string): Promise<RequestEmailChangeResult> {
        const result = await mutations.current.requestEmailChangeMutation.mutateAsync({
          targetUserId,
          newEmail,
        })
        await utils.members.getCurrentEmailChange.invalidate()
        return { ...result, expiresAt: new Date(result.expiresAt) }
      },

      async verifyEmailChange(token: string): Promise<VerifyEmailChangeResult> {
        const result = await mutations.current.verifyEmailChangeMutation.mutateAsync({ token })
        return { ...result, changedAt: new Date(result.changedAt) }
      },

      async cancelEmailChange(): Promise<void> {
        await mutations.current.cancelEmailChangeMutation.mutateAsync()
        await utils.members.getCurrentEmailChange.invalidate()
      },

      async cancelEmailChangeByToken(token: string): Promise<void> {
        await mutations.current.cancelEmailChangeByTokenMutation.mutateAsync({ token })
      },

      async getCurrentEmailChange(): Promise<PendingEmailChange | null> {
        const result = await utils.members.getCurrentEmailChange.fetch()
        if (!result) return null
        return {
          ...result,
          expiresAt: new Date(result.expiresAt),
          createdAt: new Date(result.createdAt),
        }
      },

      // ═══════════════════════════════════════════════════════════════════
      // Sign-in methods (account linking)
      // ═══════════════════════════════════════════════════════════════════

      async listSignInMethods(): Promise<SignInMethod[]> {
        const result = await utils.members.listSignInMethods.fetch()
        // biome-ignore lint/suspicious/noExplicitAny: tRPC response is untyped
        return result.map((m: any) => ({
          ...m,
          createdAt: new Date(m.createdAt),
          updatedAt: new Date(m.updatedAt),
        }))
      },

      async unlinkSignInMethod(accountId: string): Promise<void> {
        await mutations.current.unlinkSignInMethodMutation.mutateAsync({ accountId })
        await utils.members.listSignInMethods.invalidate()
      },

      // ═══════════════════════════════════════════════════════════════════
      // Ownership transfer
      // ═══════════════════════════════════════════════════════════════════

      async requestOwnershipTransfer(toUserId: string): Promise<{ expiresAt: Date }> {
        const result = await mutations.current.requestOwnershipTransferMutation.mutateAsync({
          toUserId,
        })
        await utils.members.getPendingOwnershipTransfer.invalidate()
        return { expiresAt: new Date(result.expiresAt) }
      },

      async cancelOwnershipTransfer(): Promise<void> {
        await mutations.current.cancelOwnershipTransferMutation.mutateAsync()
        await utils.members.getPendingOwnershipTransfer.invalidate()
      },

      async getPendingOwnershipTransfer(): Promise<PendingOwnershipTransfer | null> {
        const result = await utils.members.getPendingOwnershipTransfer.fetch()
        if (!result) return null
        return {
          ...result,
          expiresAt: new Date(result.expiresAt),
          createdAt: new Date(result.createdAt),
        }
      },

      async getOwnershipTransferByToken(
        token: string
      ): Promise<OwnershipTransferTokenDetails | null> {
        const result = await utils.members.getOwnershipTransferByToken.fetch({ token })
        if (!result) return null
        return { ...result, expiresAt: new Date(result.expiresAt) }
      },

      async acceptOwnershipTransfer(token: string): Promise<AcceptOwnershipTransferResult> {
        return mutations.current.acceptOwnershipTransferMutation.mutateAsync({ token })
      },

      async rejectOwnershipTransfer(token: string): Promise<{ expired: boolean }> {
        return mutations.current.rejectOwnershipTransferMutation.mutateAsync({ token })
      },
    }),
    // NUR `utils` und die Ziel-Id. Keine der 16 Mutationen — sonst ist der Client
    // pro Render neu. Die Id ist ein String aus der Route und damit stabil.
    [utils, targetUserId]
  )
}
